/**
 * Build-safe environment schema for Works Lite.
 *
 * This module contains ONLY the raw Zod shapes — no `import.meta.env` access —
 * so it can be imported from two very different contexts without drift:
 *   1. `src/config/env.ts`     — parsed against `import.meta.env` at app runtime.
 *   2. `vite.config.ts`        — validated against `loadEnv()` output at BUILD
 *                                time, so `vite build` aborts on a missing or
 *                                malformed variable (genuine build-time gate).
 *
 * Keep the two consumers in sync by editing the shape here only.
 */
import { booleanStringWithDefault as booleanString, validateEnv } from '@skewb/env';
import { z } from 'zod';

/**
 * Per-variable client schema. Every key is prefixed `VITE_` so Vite inlines it
 * into the bundle. `VITE_API_BASE_URL` is the only hard requirement; the rest
 * carry the same defaults the app used before this validation existed.
 */
export const clientSchema = {
  // API Configuration
  VITE_API_BASE_URL: z.string().url(),
  VITE_API_TIMEOUT: z.coerce.number().int().positive().default(30000),
  VITE_API_MAX_RETRIES: z.coerce.number().int().nonnegative().default(3),

  // Application Metadata
  VITE_APP_VERSION: z.string().default('0.0.1'),
  VITE_BUILD_DATE: z.string().optional(),
  VITE_ENVIRONMENT: z.enum(['development', 'qa', 'staging', 'production']).default('development'),

  // Feature Flags
  VITE_ENABLE_ANALYTICS: booleanString(false),
  VITE_ENABLE_DEBUG: booleanString(true),
  VITE_ENABLE_SERVICE_WORKER: booleanString(false),
  VITE_ENABLE_MOCK_API: booleanString(false),

  // Logging Configuration
  VITE_LOG_LEVEL: z.enum(['debug', 'info', 'warn', 'error', 'none']).default('info'),
  VITE_ENABLE_CONSOLE_LOGS: booleanString(true),

  // External Services
  VITE_ANALYTICS_ID: z.string().default(''),
  VITE_ERROR_TRACKING_DSN: z.string().default(''),
  VITE_MAP_API_KEY: z.string().default(''),

  // Performance & Optimization
  VITE_ENABLE_REACT_QUERY_DEVTOOLS: booleanString(true),
  VITE_CACHE_DURATION: z.coerce.number().int().nonnegative().default(300000),
  VITE_CACHE_STALE_TIME: z.coerce.number().int().nonnegative().default(60000),

  // Security
  VITE_ENABLE_CREDENTIALS: booleanString(true),
  VITE_AUTH_HEADER_NAME: z.string().default('Authorization'),

  // UI Configuration
  VITE_DEFAULT_PAGE_SIZE: z.coerce.number().int().positive().default(20),
  VITE_DEFAULT_THEME: z.enum(['light', 'dark', 'system']).default('light'),
  VITE_DATE_FORMAT: z.string().default('DD/MM/YYYY'),
  VITE_TIME_FORMAT: z.string().default('HH:mm'),

  // Dashboard
  VITE_DASHBOARD_REFRESH_INTERVAL_MS: z.coerce.number().int().nonnegative().default(60000),
  VITE_DASHBOARD_AUTO_REFRESH: booleanString(true),
} as const;

/**
 * Object schema consumed by both the Vite build-time check (`validateClientEnv`)
 * and the app-runtime parse in `env.ts`.
 */
export const clientEnvSchema = z.object(clientSchema);

export type ClientEnv = z.infer<typeof clientEnvSchema>;

/**
 * Validate a plain record of env vars (e.g. Vite's `loadEnv()` output) at build
 * time. Throws an aggregated error listing every problem so `vite build` fails
 * loudly with actionable detail. Returns the parsed/typed values on success.
 */
export function validateClientEnv(rawEnv: Record<string, string | undefined>): ClientEnv {
  return validateEnv(
    clientEnvSchema,
    rawEnv,
    'build',
    'Check your .env file against .env.example.',
  );
}
